/**
 * Component for assigning a bike to a case
 */
import React, { useState, useEffect } from 'react';
import { Button, Modal } from '../../../shared/components';
import { useCase } from '../hooks/useCases';
import { assignBikeToCase } from '../api/caseApi';

/**
 * Props for the AssignBike component
 */
interface AssignBikeProps {
  /**
   * ID of the case to assign a bike to
   */
  caseId: string;
  
  /**
   * Callback function called when a bike is successfully assigned
   */
  onSuccess: () => void;
}

/**
 * Interface for bike data
 */
interface Bike {
  id: string;
  name: string;
  model: string;
  status: string;
}

/**
 * Component that allows users to assign a bike to a case
 */
export function AssignBike({ caseId, onSuccess }: AssignBikeProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [bikes, setBikes] = useState<Bike[]>([]);
  const [selectedBikeId, setSelectedBikeId] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const { caseData } = useCase(isOpen ? caseId : null);

  /**
   * Fetch available bikes when modal opens
   */
  useEffect(() => {
    if (isOpen) {
      fetchBikes();
    }
  }, [isOpen]);

  /**
   * Fetch available bikes from the API
   */
  const fetchBikes = async () => {
    setLoading(true);
    try {
      // This would normally call a bike API function
      // For now, we'll use mock data
      const mockBikes: Bike[] = [
        { id: 'bike-1', name: 'Mountain Bike', model: 'Trek X500', status: 'available' },
        { id: 'bike-2', name: 'Road Bike', model: 'Specialized Tarmac', status: 'available' },
        { id: 'bike-3', name: 'Hybrid Bike', model: 'Giant Escape', status: 'available' }
      ];
      
      setBikes(mockBikes);
      
      // If the case already has a bike assigned, select it
      if (caseData?.assigned_bike_id) {
        setSelectedBikeId(caseData.assigned_bike_id);
      } else if (mockBikes.length > 0) {
        setSelectedBikeId(mockBikes[0].id);
      }
    } catch (err) {
      setError('Failed to fetch available bikes');
      console.error('Error fetching bikes:', err);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Open the modal
   */
  const handleOpen = () => {
    setIsOpen(true);
  };

  /**
   * Close the modal and reset state
   */
  const handleClose = () => {
    setIsOpen(false);
    setError(null);
  };

  /**
   * Handle bike selection change
   */
  const handleBikeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedBikeId(e.target.value);
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    
    try {
      await assignBikeToCase(caseId, selectedBikeId);
      onSuccess();
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to assign bike');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Button
        variant="secondary"
        size="sm"
        onClick={handleOpen}
        startIcon={
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
        }
      >
        {caseData?.assigned_bike_id ? 'Change Bike' : 'Assign Bike'}
      </Button>

      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        title="Assign Bike to Case"
        size="md"
      >
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
            {error}
          </div>
        )}
        
        {loading ? (
          <div className="flex justify-center items-center py-6">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div>
              <label htmlFor="bike" className="block text-sm font-medium text-gray-700">
                Select Bike
              </label>
              {bikes.length === 0 ? (
                <p className="mt-2 text-sm text-yellow-600">No bikes available for assignment.</p>
              ) : (
                <select
                  id="bike"
                  name="bike"
                  value={selectedBikeId}
                  onChange={handleBikeChange}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  {bikes.map(bike => (
                    <option key={bike.id} value={bike.id}>
                      {bike.name} - {bike.model}
                    </option>
                  ))}
                </select>
              )}
            </div>
            
            <div className="mt-6 flex justify-end space-x-3">
              <Button
                type="button"
                variant="light"
                onClick={handleClose}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                isLoading={isSubmitting}
                disabled={isSubmitting || bikes.length === 0}
              >
                Assign
              </Button>
            </div>
          </form>
        )}
      </Modal>
    </>
  );
}
